import React, { useState, useEffect } from 'react';
import {
  PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, leaderboardApi, chartApi } from '../api';

// ─── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, color, sub }) => (
  <div style={{ ...cardSt, borderTop: `3px solid ${color}` }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
        <div style={{ fontSize: '1.9rem', fontWeight: '800', color: 'var(--text-primary)', lineHeight: 1.1 }}>{value ?? '—'}</div>
        {sub && <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', marginTop: '5px' }}>{sub}</div>}
      </div>
      <div style={{ fontSize: '1.8rem' }}>{icon}</div>
    </div>
  </div>
);

const cardSt = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-lg)',
  padding: '20px 24px',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px 14px' }}>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: '6px' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontSize: '0.85rem', fontWeight: 600 }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};


// ─── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const { user } = useAuth();

  const [stats,     setStats]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [leaders,   setLeaders]   = useState([]);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      dashboardApi.getStats(),
      leaderboardApi.getStreak().catch(() => ({ data: { data: [] } })),
      chartApi.getChart().catch(() => ({ data: { data: [] } })),
    ])
      .then(([statsRes, leaderRes, chartRes]) => {
        if (cancelled) return;
        const s = statsRes.data?.data || statsRes.data;
        setStats(s);
        setLeaders((leaderRes.data?.data || []).slice(0, 5));
        setChartData(chartRes.data?.data || chartRes.data || []);
        setError(null);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.data?.message || 'Failed to load dashboard data. Check backend connection.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: '16px' }}>
      <div style={spinSt} />
      <p style={{ color: 'var(--text-muted)' }}>Loading dashboard...</p>
    </div>
  );

  if (error) return (
    <div className="animate-in">
      <div style={{ ...cardSt, color: 'var(--accent-danger)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span>⚠️ {error}</span>
        <button onClick={() => window.location.reload()} style={{ marginLeft: 'auto', color: 'var(--accent-primary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.88rem' }}>
          Retry
        </button>
      </div>
    </div>
  );

  const userStatusData = [
    { name: 'Active',   value: stats?.activeUsers   || 0, color: '#22c55e' },
    { name: 'Inactive', value: stats?.inactiveUsers || 0, color: '#f59e0b' },
    { name: 'Banned',   value: stats?.bannedUsers   || 0, color: '#ef4444' },
  ];

  return (
    <div className="animate-in">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Home</span> / <span className="current">Dashboard</span></div>
          <h1>Welcome back, {user?.name?.split(' ')[0]} 👋</h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
            Here's what's happening with WalkBattle today.
          </p>
        </div>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', textAlign: 'right' }}>
          <div>{new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</div>
          <div style={{ marginTop: '4px', color: 'var(--accent-primary)', fontWeight: 600 }}>
            {user?.adminRole?.replace('_', ' ')}
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <StatCard label="Total Users"         value={stats?.totalUsers?.toLocaleString()}   icon="👥" color="#6366f1" sub={`+${stats?.newUsersToday || 0} today`} />
        <StatCard label="Active Users"        value={stats?.activeUsers?.toLocaleString()}  icon="✅" color="#22c55e" />
        <StatCard label="Inactive"            value={stats?.inactiveUsers}                  icon="😴" color="#f59e0b" />
        <StatCard label="Banned"              value={stats?.bannedUsers}                    icon="🚫" color="#ef4444" />
        <StatCard label="Total Battles"       value={stats?.totalBattles}                   icon="⚔️" color="#8b5cf6" sub={`${stats?.activeBattles || 0} live now`} />
        <StatCard label="Total Walks"         value={stats?.totalWalks?.toLocaleString()}   icon="🚶" color="#06b6d4" />
        <StatCard label="Pending Withdrawals" value={stats?.pendingWithdrawals}              icon="💸" color={stats?.pendingWithdrawals > 0 ? '#ef4444' : '#22c55e'} />
        <StatCard label="New This Week"       value={stats?.newUsersThisWeek}               icon="🆕" color="#22c55e" />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '24px' }}>

        {/* 7-Day Activity Chart */}
        <div style={cardSt}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>📈 7-Day Activity</h3>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', background: 'var(--bg-tertiary)', padding: '3px 8px', borderRadius: '20px' }}>Daily</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="ugGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.35}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="wkGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="btGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }} />
              <Area type="monotone" dataKey="newUsers"   name="New Users"  stroke="#6366f1" strokeWidth={2} fill="url(#ugGrad)" dot={false} />
              <Area type="monotone" dataKey="walks"      name="Walks"      stroke="#22c55e" strokeWidth={2} fill="url(#wkGrad)" dot={false} />
              <Area type="monotone" dataKey="battles"    name="Battles"    stroke="#f59e0b" strokeWidth={2} fill="url(#btGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Status Pie */}
        <div style={cardSt}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '16px' }}>👥 User Status</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={userStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                dataKey="value" paddingAngle={3}>
                {userStatusData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(v, n) => [v, n]} />
              <Legend wrapperStyle={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

        {/* Battle Stats */}
        <div style={cardSt}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '16px' }}>⚔️ Battle Overview</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { label: 'Total Battles',  value: stats?.totalBattles,  color: '#6366f1' },
              { label: 'Active Now',     value: stats?.activeBattles, color: '#22c55e' },
              { label: 'Total Walks',    value: stats?.totalWalks,    color: '#06b6d4' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'var(--bg-tertiary)', borderRadius: '10px' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.88rem' }}>{item.label}</span>
                <span style={{ color: item.color, fontWeight: 700, fontSize: '1.05rem' }}>{item.value?.toLocaleString() ?? '—'}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Streak Leaders */}
        <div style={cardSt}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '16px' }}>🔥 Top Streak Leaders</h3>
          {leaders.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center', padding: '24px 0' }}>No leaderboard data</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {leaders.map((l, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, color: 'white', flexShrink: 0 }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</div>
                    <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)' }}>{l.city || '—'}</div>
                  </div>
                  <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: '0.88rem', flexShrink: 0 }}>
                    🔥 {l.streakDays || l.totalKm || '0'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const spinSt = {
  width: '40px', height: '40px',
  border: '3px solid var(--border-color)',
  borderTopColor: 'var(--accent-primary)',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};

export default Dashboard;
